import { useEffect, useState } from "react";
import { ExternalLink, ShoppingBag } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { Loading } from "@/components/shared";

export default function UntamedGear() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/public/page/untamed_gear").then((r) => setC(r.data?.content || {})); }, []);

  if (c === null) return <Loading />;
  const url = c.external_url || "https://www.untamedsportsgear.co.za";
  const categories = c.categories || [];

  return (
    <>
      <SEO title={c.seo_title || "Untamed Sports Gear | Official BJRC Kit & Equipment Supplier"} description={c.seo_description} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A]">
        {c.featured_image && <img src={mediaUrl(c.featured_image)} alt="Untamed Sports Gear" className="absolute inset-0 h-full w-full object-cover opacity-45" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <div className="max-w-2xl">
            {c.badge && (
              <span className="mb-5 inline-flex items-center rounded-full bg-[#E8A317] px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-[#0A1626]" data-testid="untamed-badge">
                {c.badge}
              </span>
            )}
            <h1 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">{c.heading || "Gear Up With Untamed Sports Gear"}</h1>
            <p className="mt-5 font-heading text-lg font-bold uppercase tracking-wide text-[#E8A317] sm:text-xl" data-testid="untamed-tagline">{c.tagline}</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">{c.intro}</p>
            <a href={url} target="_blank" rel="noreferrer noopener" data-testid="untamed-hero-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-7 py-3.5 font-extrabold uppercase tracking-wide text-[#0A1626] shadow-lg transition-all hover:bg-[#CC8F12] active:scale-95">
              <ShoppingBag className="h-5 w-5" /> {c.button_text || "Shop Untamed Sports Gear"}
            </a>
          </div>
        </div>
      </section>

      {/* LOGO STRIP */}
      {c.logo_url && (
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-8 sm:flex-row sm:justify-center sm:gap-6 sm:px-6 lg:px-8">
            <img src={mediaUrl(c.logo_url)} alt="Untamed Sports Gear logo" className="h-16 w-16 rounded-xl border border-slate-200 object-contain p-1" />
            <p className="text-center font-heading text-lg font-bold uppercase tracking-tight text-[#0B2A4A] sm:text-left">
              Official Kit & Equipment Partner of BJRC
            </p>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1E63D6]">Explore The Range</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-[#0B2A4A] sm:text-4xl">Product Categories</h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((cat, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer noopener" data-testid={`untamed-category-${i}`}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="relative h-52 bg-[#0B2A4A]">
                {cat.image ? <img src={mediaUrl(cat.image)} alt={cat.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#0B2A4A]">{cat.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{cat.description}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* SHOP CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#0B2A4A] px-8 py-14 text-center sm:px-16">
          <h2 className="font-heading text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">Shop The Full Range Online</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            All product browsing, orders, payments and delivery are handled securely on the Untamed Sports Gear website.
          </p>
          <a href={url} target="_blank" rel="noreferrer noopener" data-testid="untamed-shop-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-8 py-4 font-extrabold uppercase tracking-wide text-[#0A1626] transition-all hover:bg-[#CC8F12] active:scale-95">
            {c.button_text || "Shop Untamed Sports Gear"} <ExternalLink className="h-5 w-5" />
          </a>
        </div>
      </section>
    </>
  );
}
